import React, { createContext, useContext, useState, useEffect } from 'react';
import { Currency, getCurrency, DEFAULT_CURRENCY } from '@/types/currency';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (code: string) => void;
  formatCurrency: (amount: number, options?: FormatOptions) => string;
}

interface FormatOptions {
  showSymbol?: boolean;
  showCode?: boolean;
  decimals?: number;
  compact?: boolean; // For displaying large numbers like 1.5k, 2.3M
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    // Try to load from localStorage
    const savedCurrency = localStorage.getItem('stock-evo-currency');
    if (savedCurrency) {
      return getCurrency(savedCurrency);
    }
    return DEFAULT_CURRENCY;
  });

  const setCurrency = (code: string) => {
    const newCurrency = getCurrency(code);
    setCurrencyState(newCurrency);
    localStorage.setItem('stock-evo-currency', code);
  };

  const formatCurrency = (amount: number, options: FormatOptions = {}): string => {
    const {
      showSymbol = true,
      showCode = false,
      decimals = currency.decimalPlaces,
      compact = false,
    } = options;

    let value = amount;
    let suffix = '';

    // Handle compact notation for large numbers
    if (compact) {
      if (Math.abs(amount) >= 1_000_000_000) {
        value = amount / 1_000_000_000;
        suffix = 'B';
      } else if (Math.abs(amount) >= 1_000_000) {
        value = amount / 1_000_000;
        suffix = 'M';
      } else if (Math.abs(amount) >= 1_000) {
        value = amount / 1_000;
        suffix = 'k';
      }
    }

    // Format the number with proper separators
    const parts = value.toFixed(decimals).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, currency.thousandsSeparator);
    const decimalPart = parts[1];
    
    let formattedNumber = integerPart;
    if (decimals > 0 && decimalPart) {
      formattedNumber += currency.decimalSeparator + decimalPart;
    }

    // Add suffix for compact notation
    if (suffix) {
      formattedNumber += suffix;
    }

    // Build the final string
    let result = formattedNumber;

    if (showSymbol) {
      if (currency.symbolPosition === 'before') {
        result = currency.symbol + result;
      } else {
        result = result + ' ' + currency.symbol;
      }
    }

    if (showCode) {
      result = result + ' ' + currency.code;
    }

    return result;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
